import React from 'react';

import { Alert } from '@mui/material';

import { BOARD_TYPE_LABELS, BOARD_TYPE_ROOM_ONLY } from '~/consts/ratePlans';

import { getMissingMealKeywords } from '~/utils/inclusionMealKeywords';

interface Props {
  roomRate: App.RoomRate;
  inclusions?: Array<App.RoomRateInclusion>;
}

const getInclusionTexts = (roomRate: App.RoomRate, inclusions?: Array<App.RoomRateInclusion>): Array<string> => {
  const ratePlan = roomRate.rate_plan;
  const structuredInclusions = (inclusions ?? []).filter((inclusion) => !inclusion.lux_plus_tier);

  if (structuredInclusions.length > 0) {
    return structuredInclusions.map((inclusion) => inclusion.description ?? inclusion.name ?? '');
  }

  const texts: Array<string> = [];

  if (ratePlan.inclusions?.trim()) {
    ratePlan.inclusions
      .split('\n')
      .map((line) => line.trim())
      .filter(Boolean)
      .forEach((line) => texts.push(line.startsWith('- ') ? line.slice(2) : line));
  }

  (ratePlan.inclusions_override ?? []).forEach((item) => {
    const text = (item as { text?: unknown })?.text;
    if (typeof text === 'string' && text.trim()) {
      texts.push(text);
    }
  });

  return texts;
};

export default function RoomRateInclusionsMismatchWarning({ roomRate, inclusions }: Props) {
  const ratePlan = roomRate.rate_plan;
  const boardType = ratePlan?.board_type;

  if (!boardType || boardType === BOARD_TYPE_ROOM_ONLY) {
    return null;
  }

  const boardLabel = BOARD_TYPE_LABELS[boardType] ?? boardType;
  const inclusionTexts = getInclusionTexts(roomRate, inclusions);

  if (inclusionTexts.length === 0) {
    return (
      <Alert severity="warning" sx={{ mb: 2 }}>
        This room rate&apos;s rate plan is set to <strong>{boardLabel}</strong>, but no inclusion is configured for it
        at all (no room rate inclusions, and the rate plan has no inclusions text or override). The property page will
        still group and label this rate as {boardLabel}, but the booking will not actually include it. Add a matching
        inclusion on the Inclusions tab, or correct the rate plan&apos;s board type.
      </Alert>
    );
  }

  const missingKeywords = getMissingMealKeywords(boardType, inclusionTexts);

  if (missingKeywords.length === 0) {
    return null;
  }

  return (
    <Alert severity="warning" sx={{ mb: 2 }}>
      This room rate&apos;s rate plan is set to <strong>{boardLabel}</strong>, but no inclusion mentions{' '}
      <strong>{missingKeywords.join(', ')}</strong>. The property page will still group and label this rate as{' '}
      {boardLabel}, but the guest may not actually receive that at the property. Check the Inclusions tab, or correct
      the rate plan&apos;s board type.
    </Alert>
  );
}
